import { doAdminMutation, doAdminQueries, IQueryInfo } from 'infrastructure/api'
import { IMasterCurricula } from 'infrastructure/api'
import { useTreeGetter } from 'shared/hooks/trees/hooks'
import { getGradeId, getSubjectId } from 'shared/utils/tree'
import { nodeAttribution } from '../../utility/TreeHelpers'
import { useCallback } from 'react'
import { useAtomValue, useSetAtom } from 'infrastructure/state/jotai.wrapper'
import {
  allPartnersByName,
  allProductsWithPrimaryTreeAtom,
} from 'shared/api/masters'

export async function addProductsToGroup(
  { id: group_id }: { id: number },
  product_ids: number[],
) {
  const query: IQueryInfo = {
    resourceData: { key: 'addProductToGroup' },
    params: { group_id, product_ids },
  }
  const { data, errors } = await doAdminQueries<boolean>([query], false)
  return { result: data?.addProductToGroup, errors }
}

export async function removeProductFromGroup(
  group: { id: number },
  productId: number,
) {
  const query: IQueryInfo = {
    resourceData: { key: 'removeProductFromGroup' },
    params: { group_id: group.id, product_id: productId },
  }
  const { data, errors } = await doAdminQueries<boolean>([query], false)
  return { result: data?.removeProductFromGroup, errors }
}

export function useProductParams() {
  const getter = useTreeGetter()
  const partners = useAtomValue(allPartnersByName)
  return useCallback(
    async (masterData: IMasterCurricula) => {
      const { version: versionId, treeId, title } = masterData
      const { tree } = await getter({ versionId, treeId })
      if (!tree)
        return {
          error: 'Missing Tree',
        }
      const attribution = nodeAttribution(tree) ?? ''
      const partner = partners[attribution]
      const grade_id = getGradeId(tree)
      const subject_id = getSubjectId(tree)
      if (!partner) return { error: `Missing partner: ${attribution}` }
      if (!grade_id) return { error: `Missing grade_id: ${attribution}` }
      if (!subject_id) return { error: `Missing subject_id: ${attribution}` }
      return {
        params: {
          resourceData: { key: 'createProduct' },
          params: {
            title,
            grade_id,
            subject_id,
            primary_tree_tree_id: treeId,
            partner_id: partner.id,
          },
        },
      }
    },
    [partners],
  )
}

export type SwapResult =
  | { success: true }
  | {
      success: false
      isDuplicate: boolean
      messages: string[]
      conflictingProductId?: number
      conflictingProductTitle?: string
    }

export function useSwapEditableTreeWithRefresh() {
  const productRefresh = useSetAtom(allProductsWithPrimaryTreeAtom)
  const allProducts = useAtomValue(allProductsWithPrimaryTreeAtom)
  return useCallback(
    async (
      productId: number,
      treeIds: { treeId: string; versionId: string },
    ): Promise<SwapResult> => {
      const { errors } = await swapEditableTree(productId, treeIds)
      if (!errors?.length) {
        productRefresh()
        return { success: true }
      }
      const isDuplicate = errors.some((e: { message: string }) =>
        e.message?.includes('Duplicate entry'),
      )
      const conflicting = isDuplicate
        ? allProducts?.find(
            (p) =>
              p.id !== productId &&
              p.editable_tree?.tree_id === treeIds.treeId &&
              p.editable_tree?.version_id === treeIds.versionId,
          )
        : undefined
      return {
        success: false,
        isDuplicate,
        messages: errors.map(
          (e: { message: string }) => e.message ?? 'An unknown error occurred.',
        ),
        conflictingProductId: conflicting?.id,
        conflictingProductTitle: conflicting?.title,
      }
    },
    [productRefresh, allProducts],
  )
}

async function swapEditableTree(
  product_id: number,
  {
    treeId: tree_id,
    versionId: version_id,
  }: { treeId: string; versionId: string },
) {
  return doAdminMutation(
    {
      key: 'swapProductEditableTree',
    },
    {
      product_id,
      tree_id,
      version_id,
    },
    false,
  )
}

export function useRemoveEditableTreeWithRefresh() {
  const productRefresh = useSetAtom(allProductsWithPrimaryTreeAtom)
  return useCallback(
    async (editableTreeId: number) => {
      const { errors } = await removeEditableTree(editableTreeId)
      if (!errors?.length) {
        productRefresh()
      }
      return { errors }
    },
    [productRefresh],
  )
}

async function removeEditableTree(editableTreeId: number) {
  return doAdminMutation(
    {
      key: 'deleteEditableTree',
    },
    {
      id: editableTreeId,
    },
    false,
  )
}

export async function markProductAsAssignable({ id }: { id: number }) {
  const query: IQueryInfo = {
    resourceData: { key: 'markProductAsAssignable' },
    params: { id },
  }
  const { data, errors } = await doAdminQueries<boolean>([query], false)
  return { result: data?.markProductAsAssignable, errors }
}

export async function markProductAsUnassignable({ id }: { id: number }) {
  const query: IQueryInfo = {
    resourceData: { key: 'markProductAsUnassignable' },
    params: { id },
  }
  const { data, errors } = await doAdminQueries<boolean>([query], false)
  return { result: data?.markProductAsUnassignable, errors }
}
